import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Dialog state management with localStorage persistence
 * Persists dialog open/close state across navigation and page refreshes
 * 
 * @param storageKey - Unique key for localStorage (e.g., 'edit-payment-dialog')
 * @param initialState - Default state if no persisted value exists
 * 
 * @example
 * const editDialog = useDialogStatePersisted('edit-payment-dialog');
 * 
 * <Button onClick={editDialog.open}>Edit</Button>
 * <Dialog open={editDialog.isOpen} onOpenChange={editDialog.setIsOpen}>
 *   <Button onClick={editDialog.close}>Cancel</Button>
 * </Dialog>
 */
export function useDialogStatePersisted(
  storageKey: string,
  initialState: boolean = false
) {
  const key = `dialog-state-${storageKey}`;
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Get initial value from localStorage
  const getInitialValue = useCallback(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialState;
    } catch {
      return initialState;
    }
  }, [key, initialState]);

  // Use useState for immediate UI updates
  const [isOpen, setIsOpenInternal] = useState(getInitialValue);

  // Debounced localStorage persistence
  const persistToStorage = useCallback(
    (open: boolean) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        try {
          localStorage.setItem(key, JSON.stringify(open));
        } catch (error) {
          console.warn('Failed to save dialog state:', error);
        }
      }, 100);
    },
    [key]
  );

  const setIsOpen = useCallback(
    (open: boolean) => {
      // Immediate UI update
      setIsOpenInternal(open);
      // Background localStorage persistence
      persistToStorage(open);
    },
    [persistToStorage]
  );

  const open = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const close = useCallback(() => {
    // Immediate state update
    setIsOpenInternal(false);
    
    // Sync write to localStorage to prevent race condition with query invalidation
    try {
      localStorage.setItem(key, JSON.stringify(false));
    } catch (error) {
      console.warn('Failed to save dialog state:', error);
    }
    
    // Clear any pending debounced write
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [key]);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isOpen,
    setIsOpen,
    open,
    close,
    toggle,
  };
}
